import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Flame, Gift, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { bestPromo, fetchActivePromos, promoLabel, validatePromoCode } from "@/lib/promo";
import { PageHero, Reveal, PhotoSlot, EmptyState, UniverseSection, FaqSection, siteText, useSettings, ItemCarousel } from "@/components/shared";
import CustomSections from "@/components/layout/CustomSections";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LUNCH = ["11:45", "12:00", "12:15", "12:30", "12:45", "13:00", "13:15", "13:30", "13:45", "14:00"];
const DINNER = ["19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30", "23:00"];

const fmt = (n) => n.toFixed(2).replace(".", ",") + " €";

export default function Comptoir() {
  const settings = useSettings();
  const heroCfg = settings?.page_heroes?.comptoir || {};
  const [products, setProducts] = useState(null);
  const [active, setActive] = useState("tous");
  const [cart, setCart] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", time: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [autoPromos, setAutoPromos] = useState([]);
  const [codePromo, setCodePromo] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [gallery, setGallery] = useState([]);
  const [sort, setSort] = useState("pertinence");
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api
      .get("/shop/products")
      .then((r) => {
        setProducts(r.data);
        r.data.slice(0, 12).forEach((p) => trackEvent("product_view", { name: p.name, path: "/comptoir" }));
      })
      .catch(() => setProducts([]));
    fetchActivePromos("comptoir").then(setAutoPromos);
    api
      .get("/shop/best-sellers")
      .then((r) => setBestSellers(r.data))
      .catch(() => {});
    api
      .get("/gallery")
      .then((r) => setGallery(r.data.filter((g) => g.scope === "comptoir" && !g.variant)))
      .catch(() => {});
  }, []);

  const categories = ["tous", ...new Set((products || []).map((p) => p.category).filter(Boolean))];
  const sorted = [...(products || [])].sort((a, b) => {
    if (sort === "prix_asc") return a.price - b.price;
    if (sort === "prix_desc") return b.price - a.price;
    if (sort === "nouveautes") return (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0);
    return (a.order || 0) - (b.order || 0);
  });
  const shown = sorted.filter((p) => active === "tous" || p.category === active);
  const featured = (products || []).filter((p) => p.featured);
  const bestSellerProducts = bestSellers
    .map((bs) => (products || []).find((p) => p.id === bs.product_id))
    .filter(Boolean);
  const cartList = Object.values(cart);
  const total = cartList.reduce((s, c) => s + c.item.price * c.qty, 0);
  const { promo, discount } = bestPromo(autoPromos, codePromo, total);
  const totalAfterPromo = Math.round((total - discount) * 100) / 100;
  const percentPromo = promo?.type === "percent" ? promo : null;

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    try {
      const p = await validatePromoCode(codeInput.trim(), "comptoir");
      setCodePromo(p);
      toast.success(`Code « ${p.name} » appliqué.`);
    } catch {
      setCodePromo(null);
      toast.error("Code promotionnel invalide ou expiré.");
    }
  };

  const add = (item) =>
    setCart((c) => {
      const current = c[item.id]?.qty || 0;
      const max = item.stock === null || item.stock === undefined ? Infinity : item.stock;
      if (current >= max) {
        toast.error(`Stock maximum atteint pour ${item.name}.`);
        return c;
      }
      trackEvent("add_to_cart", { name: item.name, path: "/comptoir" });
      return { ...c, [item.id]: { item, qty: current + 1 } };
    });
  const dec = (id) =>
    setCart((c) => {
      const qty = (c[id]?.qty || 0) - 1;
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = { ...next[id], qty };
      return next;
    });
  const removeItem = (id) =>
    setCart((c) => {
      const next = { ...c };
      delete next[id];
      return next;
    });

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const renderProductCard = (product, i, prefix = "shop") => {
    const inCart = cart[product.id];
    return (
      <Reveal key={`${prefix}-${product.id}`} delay={(i % 2) * 0.08}>
        <div className="relative h-full rounded-lg border border-white/10 bg-coal/60 p-4" data-el="comptoir.product_card" data-testid={`${prefix}-item-${product.id}`}>
          {product.is_new && (
            <span className="absolute left-6 top-6 z-10 rounded bg-copper px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-ivory" data-testid={`${prefix}-new-${product.id}`}>
              Nouveau
            </span>
          )}
          <ItemCarousel
            photos={[product.photo, ...(product.photos || [])].filter(Boolean)}
            alt={product.name}
            ratio="aspect-square"
            testId={`shop-carousel-${product.id}`}
          />
          <div className="mt-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-ivory">{product.name}</h3>
              {product.description && (
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-ivory/45">
                  {product.description}
                </p>
              )}
            </div>
            {percentPromo ? (
              <span className="shrink-0 text-sm font-semibold text-gold" data-testid={`shop-promo-price-${product.id}`}>
                <span className="mr-2 text-xs font-normal text-ivory/35 line-through">{fmt(product.price)}</span>
                {fmt(product.price * (1 - percentPromo.value / 100))}
              </span>
            ) : (
              <span className="shrink-0 text-sm font-semibold text-gold">
                {fmt(product.price)}
              </span>
            )}
          </div>
          {product.stock !== null && product.stock !== undefined && product.stock > 0 && (
            <p className="mt-2 text-[11px] uppercase tracking-wider text-copper" data-testid={`shop-stock-${product.id}`}>
              {product.stock} restant(s)
            </p>
          )}
          <div className="mt-4">
            {inCart ? (
              <div className="flex items-center gap-2">
                <button onClick={() => dec(product.id)} aria-label="Retirer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold" data-testid={`shop-dec-${product.id}`}>
                  <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <span className="w-6 text-center text-sm font-semibold text-ivory">{inCart.qty}</span>
                <button onClick={() => add(product)} aria-label="Ajouter" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold" data-testid={`shop-inc-${product.id}`}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : product.stock !== null && product.stock !== undefined && product.stock <= 0 ? (
              <p
                className="rounded-md border border-white/10 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.15em] text-ivory/35"
                data-testid={`shop-outofstock-${product.id}`}
              >
                Rupture de stock
              </p>
            ) : (
              <button onClick={() => add(product)} className="btn-outline !h-9 w-full !px-4 !text-[11px]" data-el="comptoir.add_button" data-testid={`shop-add-${product.id}`}>
                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                Ajouter au panier
              </button>
            )}
          </div>
        </div>
      </Reveal>
    );
  };

  const pay = async () => {
    if (cartList.length === 0) {
      toast.error("Votre panier est vide.");
      return;
    }
    if (!form.name || !form.phone || !form.email || !form.date || !form.time) {
      toast.error("Merci de compléter vos coordonnées et le créneau de retrait.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/shop/orders", {
        ...form,
        items: cartList.map((c) => ({ product_id: c.item.id, quantity: c.qty })),
        promo_code: codePromo ? codeInput.trim().toUpperCase() : "",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <div data-testid="comptoir-page">
      <PageHero
        overline="Le Comptoir Maasaï"
        title={heroCfg.title || "La boutique du Palais"}
        testId="comptoir-hero"
        image={heroCfg.image || ""}
        ctas={[{ label: "Découvrir la boutique", href: "#boutique" }]}
      >
        <p>
          {heroCfg.description || siteText(settings, "comptoir_intro") ||
            "Jus de bissap, produits africains, épicerie et produits dérivés : commandez en ligne, réglez de façon sécurisée, puis récupérez vos produits au restaurant."}
        </p>
      </PageHero>

      {featured.length > 0 && (
        <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="shop-featured-section">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="overline mb-6">Nos coups de cœur</p>
              <h2 className="text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                Produits mis en avant
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-featured-grid">
              {featured.slice(0, 3).map((product, i) => renderProductCard(product, i, "featured"))}
            </div>
          </div>
        </section>
      )}

      <section id="boutique" className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_380px]">
          <div>
            {categories.length > 1 && (
              <Reveal>
                <div className="mb-10 flex flex-wrap gap-2" data-testid="shop-categories">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActive(cat)}
                      data-testid={`shop-tab-${cat}`}
                      className={`rounded-md px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                        active === cat ? "bg-copper text-ivory" : "text-ivory/50 hover:text-gold"
                      }`}
                    >
                      {cat === "tous" ? "Tous" : cat}
                    </button>
                  ))}
                  <div className="ml-auto">
                    <Select value={sort} onValueChange={setSort}>
                      <SelectTrigger className="!h-9 w-44 border-white/10 bg-white/5 text-xs text-ivory" data-testid="shop-sort-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-ivory">
                        <SelectItem value="pertinence">Pertinence</SelectItem>
                        <SelectItem value="nouveautes">Nouveautés</SelectItem>
                        <SelectItem value="prix_asc">Prix croissant</SelectItem>
                        <SelectItem value="prix_desc">Prix décroissant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Reveal>
            )}

            {products === null ? null : shown.length === 0 ? (
              <EmptyState text="Les produits arrivent bientôt." />
            ) : (
              <div className="grid gap-6 sm:grid-cols-2" data-testid="shop-grid">
                {shown.map((product, i) => renderProductCard(product, i))}
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="shop-cart">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                <ShoppingBag className="h-4 w-4 text-gold" strokeWidth={1.5} />
                Votre panier
              </h2>

              {cartList.length === 0 ? (
                <p className="mt-6 text-sm text-ivory/40" data-testid="shop-cart-empty">
                  Ajoutez des produits depuis la boutique.
                </p>
              ) : (
                <>
                <ul className="mt-6 space-y-3" data-testid="shop-cart-items">
                  {cartList.map((c) => (
                    <li key={c.item.id} className="flex items-center gap-3 text-sm">
                      <span className="text-gold">{c.qty}×</span>
                      <span className="min-w-0 flex-1 truncate text-ivory/80">{c.item.name}</span>
                      <span className="text-ivory/60">{fmt(c.item.price * c.qty)}</span>
                      <button onClick={() => removeItem(c.item.id)} aria-label="Supprimer" className="text-ivory/30 transition-colors hover:text-red-400" data-testid={`shop-cart-remove-${c.item.id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </li>
                  ))}
                </ul>
                {(() => {
                  const cartIds = new Set(cartList.map((c) => c.item.id));
                  const cartCats = new Set(cartList.map((c) => c.item.category).filter(Boolean));
                  const suggestions = (products || [])
                    .filter((p) => !cartIds.has(p.id) && (cartCats.size === 0 || cartCats.has(p.category)))
                    .filter((p) => p.stock === null || p.stock === undefined || p.stock > 0)
                    .slice(0, 3);
                  if (suggestions.length === 0) return null;
                  return (
                    <div className="mt-5 rounded-md border border-white/10 p-4" data-testid="shop-suggestions">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">
                        Vous aimerez aussi
                      </p>
                      <div className="mt-3 space-y-2.5">
                        {suggestions.map((p) => (
                          <div key={p.id} className="flex items-center gap-3" data-testid={`shop-suggestion-${p.id}`}>
                            <span className="min-w-0 flex-1 truncate text-xs text-ivory/70">{p.name}</span>
                            <span className="text-xs text-gold">{fmt(p.price)}</span>
                            <button onClick={() => add(p)} className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" aria-label={`Ajouter ${p.name}`} data-testid={`shop-suggestion-add-${p.id}`}>
                              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                </>
              )}

              <div className="mt-6 space-y-2 border-t border-white/10 pt-4">
                <div className="flex items-center gap-2">
                  <input
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    placeholder="Code promo"
                    className="input-dark !h-10 flex-1 uppercase !text-xs"
                    data-testid="shop-promo-input"
                  />
                  <button onClick={applyCode} className="btn-outline !h-10 !px-4 !text-[11px]" data-testid="shop-promo-apply">
                    Appliquer
                  </button>
                </div>
                {discount > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ivory/50">Sous-total</span>
                      <span className="text-ivory/50 line-through">{fmt(total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-testid="shop-cart-discount">
                      <span className="text-gold">{promo.name} ({promoLabel(promo)})</span>
                      <span className="text-gold">-{fmt(discount)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm text-ivory/60">Total</span>
                  <span className="text-lg font-bold text-gold" data-testid="shop-cart-total">
                    {fmt(totalAfterPromo)}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-4 border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-[0.2em] text-ivory/40">
                  Retrait au restaurant
                </p>
                <input value={form.name} onChange={update("name")} placeholder="Nom et prénom" className="input-dark !h-11" data-testid="shop-name-input" />
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <input value={form.phone} onChange={update("phone")} placeholder="Téléphone" className="input-dark !h-11" data-testid="shop-phone-input" />
                  <input type="email" value={form.email} onChange={update("email")} placeholder="Email" className="input-dark !h-11" data-testid="shop-email-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <input type="date" value={form.date} onChange={update("date")} min={new Date().toISOString().split("T")[0]} className="input-dark !h-11 [color-scheme:dark]" data-testid="shop-date-input" />
                  <Select value={form.time} onValueChange={(v) => setForm((f) => ({ ...f, time: v }))}>
                    <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="shop-time-select">
                      <SelectValue placeholder="Retrait" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-coal text-ivory">
                      <SelectGroup>
                        <SelectLabel className="text-gold">Midi</SelectLabel>
                        {LUNCH.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace(":", "h")}</SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-gold">Soir</SelectLabel>
                        {DINNER.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace(":", "h")}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <textarea value={form.notes} onChange={update("notes")} placeholder="Précisions éventuelles" rows={2} className="input-dark !h-auto py-3" data-testid="shop-notes-input" />
                <button onClick={pay} disabled={sending || cartList.length === 0} className="btn-primary w-full" data-testid="shop-pay-button">
                  <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                  {sending ? "Redirection…" : `Payer — ${fmt(totalAfterPromo)}`}
                </button>
                <p className="text-center text-[11px] text-ivory/35">Paiement sécurisé par Stripe.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {bestSellerProducts.length > 0 && (
        <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="shop-bestsellers-section">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="overline mb-6 flex items-center gap-2">
                <Flame className="h-4 w-4 text-copper" strokeWidth={1.5} />
                Les plus demandés
              </p>
              <h2 className="text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                Nos meilleures ventes
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-bestsellers-grid">
              {bestSellerProducts.map((product, i) => renderProductCard(product, i, "best"))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="shop-giftcard-banner">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="flex flex-col items-start gap-8 rounded-lg border border-gold/25 bg-coal/60 p-10 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="overline mb-4 flex items-center gap-2">
                  <Gift className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  Cartes cadeaux
                </p>
                <h2 className="max-w-xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                  Offrez l'Univers Maasaï
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-ivory/55">
                  Nos cartes cadeaux sont valables au restaurant et utilisables pour découvrir
                  toutes nos activités. Le cadeau idéal pour les amoureux de la cuisine africaine.
                </p>
              </div>
              <Link to="/cartes-cadeaux" className="btn-primary shrink-0" data-testid="shop-giftcard-link">
                Offrir une carte cadeau
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection scope="comptoir" title="Vos questions sur le Comptoir" />

      {/* Galerie dédiée au Comptoir */}
      {gallery.length > 0 && (
        <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="comptoir-gallery-section">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="overline mb-6">En images</p>
              <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                La boutique en photos
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="comptoir-gallery-grid">
              {gallery.map((g, i) => (
                <Reveal key={g.id} delay={(i % 4) * 0.06}>
                  <div className="flex h-full flex-col rounded-lg border border-white/10 bg-coal/60 p-4" data-testid={`comptoir-gallery-card-${i}`}>
                    <PhotoSlot src={g.photo} alt={g.title || g.caption || "Le Comptoir Maasaï"} ratio="aspect-[4/3]" />
                    {(g.title || g.caption) && (
                      <h3 className="mt-4 text-sm font-semibold tracking-tight text-ivory">
                        {g.title || g.caption}
                      </h3>
                    )}
                    {g.description && (
                      <p className="mt-2 flex-1 text-xs leading-relaxed text-ivory/50">{g.description}</p>
                    )}
                    <Link
                      to="/contact?activity=comptoir"
                      className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold/80 transition-colors hover:text-gold"
                      data-testid={`comptoir-gallery-contact-${i}`}
                    >
                      Contactez-nous
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CustomSections page="comptoir" />

      <UniverseSection current="comptoir" />
    </div>
  );
}

export function ComptoirSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/orders/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="comptoir-success-page">
      <PageHero
        overline="Le Comptoir Maasaï"
        title={state === "paid" ? "Commande confirmée" : "Confirmation en cours"}
        testId="comptoir-hero"
      />
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-gold/30 bg-coal/60 p-12 text-center">
              {state === "loading" && (
                <p className="text-sm text-ivory/60" data-testid="shop-payment-loading">
                  Vérification du paiement…
                </p>
              )}
              {state === "paid" && (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-8 text-2xl font-semibold tracking-tight text-ivory">Merci !</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/55" data-testid="shop-payment-confirmed">
                    Votre commande est confirmée et réglée. Un email de confirmation vient de vous
                    être envoyé. Présentez-vous au restaurant au créneau choisi pour récupérer vos
                    produits.
                  </p>
                </>
              )}
              {state === "pending" && (
                <p className="text-sm text-ivory/60" data-testid="shop-payment-pending">
                  Paiement en cours de traitement. Vous recevrez un email de confirmation.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-red-400" data-testid="shop-payment-error">
                  Impossible de vérifier le paiement. Merci de nous contacter sur WhatsApp.
                </p>
              )}
              <Link to="/" className="btn-outline mt-10" data-testid="shop-success-home">
                Retour à l'accueil
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
